import { useEffect, useRef, useState } from 'react'
import type { Layer, Spacing } from '../data/v1'

const PADDING_COLOR = 'rgba(147, 196, 125, 0.55)'
const MARGIN_COLOR = 'rgba(246, 178, 107, 0.55)'
const BOX_MODEL_LINGER_MS = 1500

function spacingChanged(a: Spacing, b: Spacing): boolean {
	return a.paddingTop !== b.paddingTop || a.paddingRight !== b.paddingRight
		|| a.paddingBottom !== b.paddingBottom || a.paddingLeft !== b.paddingLeft
		|| a.marginTop !== b.marginTop || a.marginRight !== b.marginRight
		|| a.marginBottom !== b.marginBottom || a.marginLeft !== b.marginLeft
}

export function useBoxModelFlash(layer: Layer | null): boolean {
	const [visible, setVisible] = useState(false)
	const prevSpacingRef = useRef<Spacing | null>(null)
	const prevLayerIdRef = useRef<string | null>(null)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	useEffect(() => {
		if (!layer || !('spacing' in layer)) {
			prevSpacingRef.current = null
			prevLayerIdRef.current = null
			return
		}

		const spacing = layer.spacing

		// Reset baseline when switching to a different layer — don't flash
		if (prevLayerIdRef.current !== layer.id) {
			prevLayerIdRef.current = layer.id
			prevSpacingRef.current = spacing
			return
		}

		const prev = prevSpacingRef.current
		prevSpacingRef.current = spacing

		if (!prev) { return }
		if (!spacingChanged(prev, spacing)) { return }

		setVisible(true)
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => setVisible(false), BOX_MODEL_LINGER_MS)
	}, [layer])

	useEffect(() => {
		return () => clearTimeout(timeoutRef.current)
	}, [])

	return visible
}

export function BoxModelOverlay({ layer }: { layer: Layer }) {
	if (!('spacing' in layer)) { return null }
	const { spacing } = layer

	return (
		<>
			<PaddingOverlay spacing={spacing} />
			<MarginOverlay spacing={spacing} />
		</>
	)
}

function PaddingOverlay({ spacing }: { spacing: Spacing }) {
	return (
		<>
			{spacing.paddingTop > 0 && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: spacing.paddingTop,
						background: PADDING_COLOR,
					}}
				/>
			)}
			{spacing.paddingBottom > 0 && (
				<div
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						right: 0,
						height: spacing.paddingBottom,
						background: PADDING_COLOR,
					}}
				/>
			)}
			{spacing.paddingLeft > 0 && (
				<div
					style={{
						position: 'absolute',
						top: spacing.paddingTop,
						left: 0,
						bottom: spacing.paddingBottom,
						width: spacing.paddingLeft,
						background: PADDING_COLOR,
					}}
				/>
			)}
			{spacing.paddingRight > 0 && (
				<div
					style={{
						position: 'absolute',
						top: spacing.paddingTop,
						right: 0,
						bottom: spacing.paddingBottom,
						width: spacing.paddingRight,
						background: PADDING_COLOR,
					}}
				/>
			)}
		</>
	)
}

function MarginOverlay({ spacing }: { spacing: Spacing }) {
	return (
		<>
			{spacing.marginTop > 0 && (
				<div
					style={{
						position: 'absolute',
						top: -spacing.marginTop,
						left: -spacing.marginLeft,
						right: -spacing.marginRight,
						height: spacing.marginTop,
						background: MARGIN_COLOR,
					}}
				/>
			)}
			{spacing.marginBottom > 0 && (
				<div
					style={{
						position: 'absolute',
						bottom: -spacing.marginBottom,
						left: -spacing.marginLeft,
						right: -spacing.marginRight,
						height: spacing.marginBottom,
						background: MARGIN_COLOR,
					}}
				/>
			)}
			{spacing.marginLeft > 0 && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: -spacing.marginLeft,
						bottom: 0,
						width: spacing.marginLeft,
						background: MARGIN_COLOR,
					}}
				/>
			)}
			{spacing.marginRight > 0 && (
				<div
					style={{
						position: 'absolute',
						top: 0,
						right: -spacing.marginRight,
						bottom: 0,
						width: spacing.marginRight,
						background: MARGIN_COLOR,
					}}
				/>
			)}
		</>
	)
}
